import RouteTemplate from 'ember-route-template'
import type { TOC } from '@ember/component/template-only'
import { PageHeader } from 'teacher-ui-client/components/page-header'
import { t } from 'ember-intl'
import { DataOperationsTable } from 'teacher-ui-client/components/data-operations/table'
import { FormComponentsFormSelect } from 'teacher-ui-client/components/form-components/form-select'
import { UiButton } from 'teacher-ui-client/components/ui-button'
import { InputText } from 'teacher-ui-client/components/base/input-text'
import { ManageCertificatesTable } from 'teacher-ui-client/components/manage/certificates/table'
import { ReportingPeriodSelect } from 'teacher-ui-client/components/reporting/period-select'
import type CertificatesRoute from 'teacher-ui-client/manage/certificates/route'
import type CertificatesController from 'teacher-ui-client/manage/certificates/controller'
import { fn, array } from '@ember/helper'
import { or } from 'ember-truth-helpers'
import { on } from '@ember/modifier'
import perform from 'ember-concurrency/helpers/perform'
import { FidgetSpinnerWaveComponent } from '@blakeelearning/fidget/components/fidget/spinner/wave'
import { themeKey } from 'teacher-ui-client/helpers/theme-key'
import { pick } from 'teacher-ui-client/helpers/pick'
import { NoReportValue } from 'teacher-ui-client/components/reporting/no-report-value'

interface Signature {
  Args: {
    model: ModelFor<CertificatesRoute>
    controller: CertificatesController
  }
}
export const ManageCertificatesPage: TOC<Signature> = <template>
  {{! This log is here intentionally for now, else certificates don't load locally under ember-source 5.6+ }}
  {{! Can be removed when this is resolved - https://github.com/universal-ember/reactiveweb/issues/110 }}
  {{! template-lint-disable no-log }}
  {{log @controller.fetchCertificates.value}}
  <PageHeader>
    <:title>
      <h1>
        {{t "manage.title.certificates"}}
      </h1>
    </:title>
  </PageHeader>
  <DataOperationsTable @dataOperationsConfig={{@controller.dataOperationsConfig}} as |selectRecords sorting pagination|>
    <div class="manage-certificates-table-panel border-dusty-black-100 rounded-md border">
      <div class="{{@controller.tableTheme.controlsBg}} safari-gap-2 flex items-center gap-2 p-5">
        <label>
          {{t "manage.certificates.selectProgram"}}
        </label>
        <FormComponentsFormSelect
          data-test-select-product
          @optionClick={{@controller.setProduct}}
          @value={{@controller.certificateProduct}}
          @options={{@controller.productOptions}}
        />
      </div>
      <div class="{{@controller.tableTheme.controlsBg}} safari-gap-2 flex items-center gap-2 p-5">
        {{#if @controller.isSubco}}
          <label>
            {{t "manage.certificates.show"}}
          </label>
          <FormComponentsFormSelect
            @options={{@controller.teacherOptions}}
            @valueLookupKey="value"
            @search={{true}}
            @searchPlaceholder={{t "reporting.dropdownSearchTeachers"}}
            @defaultText={{t "manage.schoolClasses.selectTeacher"}}
            @optionClick={{@controller.setTeacherId}}
            @resetAction={{fn @controller.setTeacherId ""}}
            @value={{@controller.teacherId}}
          />
        {{/if}}

        <ReportingPeriodSelect data-test-certificates-period-select />

        <FormComponentsFormSelect
          data-test-type-filter
          @optionClick={{@controller.setTypeFilter}}
          @value={{@controller.typeFilter}}
          @options={{@controller.typeOptions}}
          @defaultText={{t "manage.certificates.selectType"}}
          @resetAction={{fn @controller.setTypeFilter ""}}
        />

        <UiButton
          data-test-print-certificates
          disabled={{or selectRecords.noSelectedRecords @controller.printCertificates.isRunning}}
          {{on "click" (perform @controller.printCertificates selectRecords.selectedRecords)}}
        >
          {{#if @controller.printCertificates.isRunning}}
            <FidgetSpinnerWaveComponent @small={{true}} />
          {{else}}
            {{t "manage.certificates.printCertificates"}}
          {{/if}}
        </UiButton>

        <InputText
          class="ml-auto"
          type="search"
          role="searchbox"
          name="search-filter"
          value={{@controller.textFilter}}
          aria-label={{t "manage.certificates.filter.label"}}
          placeholder={{t "manage.certificates.filter.placeholder"}}
          @themeKey={{themeKey @model.subscriptionType}}
          @icon="search"
          @iconEnd={{true}}
          {{on "input" (pick "target.value" @controller.setTextFilter)}}
        />
      </div>
      {{#if @controller.latestCertificates}}
        <ManageCertificatesTable
          class="w-full"
          @isReloading={{@controller.fetchCertificates.isPending}}
          @themeKey={{themeKey @model.subscriptionType}}
          @certificates={{pagination.currentPageRecords}}
          @columns={{@controller.columns}}
          @onSelectAll={{selectRecords.toggleAll}}
          @isAllSelected={{selectRecords.allSelected}}
          @onSelectId={{selectRecords.toggle}}
          @selectedIds={{selectRecords.selectedIds}}
          @onUpdateSorting={{sorting.sort}}
          @sorting={{sorting}}
          @printCertificates={{@controller.printCertificates}}
        />
      {{else}}
        <NoReportValue class="p-3" @reportStates={{array @controller.fetchCertificates}} />
      {{/if}}
    </div>
  </DataOperationsTable>
</template>

export default RouteTemplate(ManageCertificatesPage)
